import type { ExtraPriceType, SizeLocales } from "../../lib/constants/filtersConstants";
import type { TCountryCode, TLanguageCode } from "countries-list";

export type CountryCode = TCountryCode;
export type LanguageCode = TLanguageCode;

export interface EccoClubConfig {
    hasPointsAndTiers: boolean;
}

export interface MerchantReturnPolicyConfig {
    returnPolicyCategory:
        | "https://schema.org/MerchantReturnFiniteReturnWindow"
        | "https://schema.org/MerchantReturnNotPermitted"
        | "https://schema.org/MerchantReturnUnlimitedWindow"
        | "https://schema.org/MerchantReturnUnspecified";
    merchantReturnDays: number;
    returnMethod:
        | "https://schema.org/ReturnByMail"
        | "https://schema.org/ReturnInStore"
        | "https://schema.org/ReturnAtKiosk";
    returnFees:
        | "https://schema.org/FreeReturn"
        | "https://schema.org/ReturnFeesCustomerResponsibility"
        | "https://schema.org/ReturnShippingFees";
}

export enum Market {
    MY = "my",
    SG = "sg",
    ES = "es",
    EE = "ee",
    IN = "in",
    KR = "kr",
    CA = "ca",
    US = "us",
    AU = "au",
    JP = "jp",
    GB = "gb",
    PL = "pl",
    PT = "pt",
    HU = "hu",
    CZ = "cz",
    HR = "hr",
    BE = "be",
    SI = "si",
    SK = "sk",
    FR = "fr",
    AT = "at",
    CH = "ch",
    DE = "de",
    NL = "nl",
    DK = "dk",
    FI = "fi",
    IE = "ie",
    LT = "lt",
    LV = "lv",
    NO = "no",
    SE = "se",
    NZ = "nz",
    CY = "cy",
}

export interface SiteConfigEntry {
    country: CountryCode;
    /** English, for internal use and English-language markets. */
    countryName: string;
    /**
     * The country's name in its own language, for showing a shopper where they are ordering to.
     * A market serving two languages (BE, CH) can only carry one, so it holds the first language
     * its `language` array lists.
     */
    localizedCountryName: string;
    language: LanguageCode[];
    cmsLocaleTagId: string;
    currency: string;
    fractionDigits: number;
    roundingUnit?: number;
    shoeSizingConventions: SizeLocales[];
    sizeGuideCollections: SizeLocales[];
    checkout: {
        countryCode: string;
        requiresSalesTaxEstimation?: boolean;
        // Storefront prices are tax-inclusive (e.g. JP). Drives order-confirmation
        // display: line active price and subtotal use gross (inc-tax) instead of net.
        hasTaxInclusivePricing?: boolean;
        hasShippingFees?: boolean;
        hasShippingDetails?: boolean;
        minAddressLookupLength?: number;
        hasDHLExtraFields?: boolean;
        parcelShops?: {
            searchMethod: "address" | "latlng";
        };
        hasFooter?: boolean;
    };
    /**
     * Get geo data from https://github.com/mihai-craita/countries_center_box/blob/master/countries.json
     */
    geo: {
        centerLat: number;
        centerLng: number;
        swLat: number;
        swLng: number;
        neLat: number;
        neLng: number;
        initZoomLevel?: number;
        imperialMetricSystem?: boolean;
    };
    hasLocalizedCategoryFacets: boolean;
    orderNumberFormatRegex?: RegExp;
    reCaptchaIntegration: {
        contactForm: boolean;
        checkout: boolean;
        signupForm: boolean;
    };
    liveChat: boolean;
    isCountryChangeForBillingAddressAllowed?: boolean;
    isOrderHistoryRoutesAllowed?: boolean;
    isGiftWrappingAllowed?: boolean;
    isProductInfoRequired?: boolean;
    includesAllTaxes?: boolean;
    amazonPay?: {
        enabled: boolean;
        bannerEnabled: boolean;
        merchantId: string;
        region: string;
        /**
         * Checkout JS URL is different from region to region. For more info, refer to: https://developer.amazon.com/docs/amazon-pay-checkout/v1-amazon-pay-script.html#amazon-pay-script
         */
        checkoutJsUrl: string;
    };
    supportsGlobalPrivacyControl?: boolean;
    isMarketWithLoyalty?: boolean;
    onKlarnaSiteMessaging?: boolean;
    extraPriceType?: ExtraPriceType[];
    trustpilotIntegration?: boolean;
    showParcelLockerId?: boolean;
    hideParcelLockerWorkingHours?: boolean;
    hideAvailabilityInStore?: boolean;
    hasRaffle: boolean;
    hasNotifySizeAvailability?: boolean;
    hasGrabPayWidget?: boolean;
    promotionalThemeColor?: string;
    hasNonExpressDeliveryProducts?: boolean;
    hasAccessibilityStatementLink?: boolean;
    eccoClubConfig?: EccoClubConfig;
    merchantReturnDays?: number;
    showShippingNetCost?: boolean;
}
